// Card de KPI do Dashboard — bloco visual compartilhado pelas
// seções Engajamento (funil) e Operação: ícone em círculo suave da
// cor semântica, valor grande tabular-nums, sub-linha opcional
// (taxa do funil) e delta vs dia anterior com seta. TODO KPI é
// clicável → Consulta pré-filtrada (deep-link).
import type { LucideIcon } from "lucide-react"
import { ArrowDownIcon, ArrowUpIcon } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { Card, CardContent } from "@/components/ui/card"
import { cn } from "@/lib/utils"

import {
  buildNotificationSearchHref,
  formatDashboardDelta,
  type NotificationSearchLinkFilters,
} from "../dashboard-utils"

// Tom semântico do card: pinta o círculo do ícone (bg soft) e, no
// destructive, o card inteiro (alerta de falhas acima do limiar).
export type DashboardKpiTone =
  | "primary"
  | "success"
  | "info"
  | "warning"
  | "destructive"

const TONE_ICON_CLASSES: Record<DashboardKpiTone, string> =
  {
    primary: "bg-primary/10 text-primary",
    success: "bg-emerald-100 text-emerald-700",
    info: "bg-sky-100 text-sky-700",
    warning: "bg-amber-100 text-amber-700",
    destructive: "bg-red-100 text-red-700",
  }

interface DashboardKpiCardProps {
  icon: LucideIcon
  tone: DashboardKpiTone
  label: string
  // Valor já formatado no locale (tabular-nums no CSS).
  value: string
  // Sub-linha opcional (ex.: "Taxa de entrega: 97,3%").
  subline?: string
  // Delta vs período anterior (fração); null/undefined = sem delta.
  delta?: number | null
  // Rótulo do delta já resolvido ("vs dia anterior" no intradiário,
  // "vs período anterior" no histórico). Vem do container.
  deltaLabel: string
  // Aumento é ruim (falhas/fila): inverte a semântica da cor.
  isBadWhenUp?: boolean
  // Deep-link → Consulta pré-filtrada.
  filters: NotificationSearchLinkFilters
  ariaLabel: string
}

const DashboardKpiDelta = ({
  delta,
  deltaLabel,
  isBadWhenUp,
}: {
  delta: number
  deltaLabel: string
  isBadWhenUp?: boolean
}) => {
  const { i18n } = useTranslation()
  // Semântica da cor: subir é bom — EXCETO quando isBadWhenUp
  // (falhas/fila). Delta zero fica neutro.
  const isPositiveTone =
    delta === 0 ? null : delta > 0 !== Boolean(isBadWhenUp)
  const ArrowIcon = delta >= 0 ? ArrowUpIcon : ArrowDownIcon
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 text-xs font-medium tabular-nums",
        isPositiveTone === null
          ? "text-muted-foreground"
          : isPositiveTone
            ? "text-emerald-700"
            : "text-red-700"
      )}
    >
      {delta !== 0 && (
        <ArrowIcon
          aria-hidden="true"
          className="size-3 shrink-0"
        />
      )}
      {formatDashboardDelta(delta, i18n.language)}{" "}
      <span className="font-normal text-muted-foreground">
        {deltaLabel}
      </span>
    </span>
  )
}

export const DashboardKpiCard = ({
  icon: Icon,
  tone,
  label,
  value,
  subline,
  delta,
  deltaLabel,
  isBadWhenUp,
  filters,
  ariaLabel,
}: DashboardKpiCardProps) => {
  const isDestructive = tone === "destructive"
  return (
    <Link
      to={buildNotificationSearchHref(filters)}
      aria-label={ariaLabel}
      className="rounded-md outline-offset-2 transition-shadow hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
    >
      <Card
        className={cn(
          "h-full",
          isDestructive && "border-red-200 bg-red-50"
        )}
      >
        <CardContent className="flex items-start gap-3 p-4">
          <span
            aria-hidden="true"
            className={cn(
              "flex size-10 shrink-0 items-center justify-center rounded-full",
              TONE_ICON_CLASSES[tone]
            )}
          >
            <Icon className="size-5" />
          </span>
          <span className="flex min-w-0 flex-col gap-0.5">
            <span
              className={cn(
                "text-sm font-medium text-muted-foreground",
                isDestructive && "text-red-700"
              )}
            >
              {label}
            </span>
            <span
              className={cn(
                "text-2xl font-semibold tabular-nums",
                isDestructive && "text-red-700"
              )}
            >
              {value}
            </span>
            {subline && (
              <span className="text-xs tabular-nums text-muted-foreground">
                {subline}
              </span>
            )}
            {delta !== null && delta !== undefined && (
              <DashboardKpiDelta
                delta={delta}
                deltaLabel={deltaLabel}
                isBadWhenUp={isBadWhenUp}
              />
            )}
          </span>
        </CardContent>
      </Card>
    </Link>
  )
}
